import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Paper,
  Divider,
} from '@mui/material';
import {
  Phone,
  Email,
  Event,
  Person,
  AccessTime,
  Business,
} from '@mui/icons-material';
import { format, isValid, isToday, isTomorrow, addDays } from 'date-fns';

const UpcomingFollowups = ({ followups = [] }) => {
  // Priority color mapping
  const getPriorityColor = (priority) => {
    const priorityColors = {
      'high': 'error',
      'medium': 'warning',
      'low': 'success',
      'urgent': 'error',
      'normal': 'primary',
    };
    
    const normalizedPriority = priority?.toLowerCase();
    return priorityColors[normalizedPriority] || 'default';
  };

  // Format date with relative indicators
  const formatFollowupDate = (dateString) => {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    if (!isValid(date)) return 'Invalid Date';

    if (isToday(date)) {
      return `Today, ${format(date, 'h:mm a')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow, ${format(date, 'h:mm a')}`;
    } else if (date <= addDays(new Date(), 7)) {
      return format(date, 'EEEE, h:mm a');
    } else {
      return format(date, 'MMM dd, h:mm a');
    }
  };

  // Get date badge color based on urgency
  const getDateBadgeColor = (dateString) => {
    if (!dateString) return 'default';
    const date = new Date(dateString);
    if (!isValid(date)) return 'default';

    if (isToday(date)) return 'error';
    if (isTomorrow(date)) return 'warning';
    return 'primary';
  };

  // Get initials for avatar
  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || '?';
  };

  // Handle empty state
  if (!followups || followups.length === 0) {
    return (
      <Card sx={{ height: '100%', minHeight: 300 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Upcoming Follow-ups
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 200,
              color: 'text.secondary',
            }}
          >
            <Event sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1">
              No upcoming follow-ups
            </Typography>
            <Typography variant="caption" sx={{ mt: 1 }}>
              Scheduled follow-ups will appear here
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Sort follow-ups by date
  const sortedFollowups = [...followups].sort((a, b) => {
    const dateA = new Date(a.follow_up_date || a.scheduled_date);
    const dateB = new Date(b.follow_up_date || b.scheduled_date);
    return dateA - dateB;
  });

  return (
    <Card sx={{ height: '100%', minHeight: 300 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Upcoming Follow-ups
          </Typography>
          <Chip
            label={`${followups.length} Total`}
            size="small"
            variant="outlined"
            color="primary"
          />
        </Box>
        
        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          <Grid container spacing={2}>
            {sortedFollowups.slice(0, 8).map((followup, index) => (
              <Grid item xs={12} sm={6} key={followup.id || index}>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    height: '100%',
                    border: 1,
                    borderColor: 'divider',
                    '&:hover': {
                      boxShadow: 2,
                      borderColor: 'primary.main',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  {/* Header with lead info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 2,
                        bgcolor: 'secondary.main',
                        fontSize: '0.875rem'
                      }}
                    >
                      {followup.name?.charAt(0)?.toUpperCase() ||
                       followup.lead?.name?.charAt(0)?.toUpperCase() ||
                       getInitials(
                         followup.lead?.first_name || followup.first_name,
                         followup.lead?.last_name || followup.last_name
                       )}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        {followup.name ||
                         followup.lead?.name ||
                         `${followup.lead?.first_name || followup.first_name || ''} ${followup.lead?.last_name || followup.last_name || ''}`.trim() ||
                         'Unknown Lead'}
                      </Typography>
                      {(followup.lead?.company || followup.company) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <Business sx={{ fontSize: 12, mr: 0.5, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {followup.lead?.company || followup.company}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {followup.priority && (
                      <Chip
                        label={followup.priority}
                        size="small"
                        color={getPriorityColor(followup.priority)}
                        variant="outlined"
                      />
                    )}
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* Follow-up details */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AccessTime sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Chip
                        label={formatFollowupDate(followup.follow_up_date || followup.scheduled_date)}
                        size="small"
                        color={getDateBadgeColor(followup.follow_up_date || followup.scheduled_date)}
                        variant="filled"
                      />
                    </Box>
                    
                    {followup.notes && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {followup.notes.length > 80 ? `${followup.notes.substring(0, 80)}...` : followup.notes}
                      </Typography>
                    )}
                  </Box>

                  {/* Action buttons */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    {(followup.lead?.phone || followup.phone) && (
                      <Tooltip title="Call Lead">
                        <IconButton size="small" color="success">
                          <Phone fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(followup.lead?.email || followup.email) && (
                      <Tooltip title="Send Email">
                        <IconButton size="small" color="info">
                          <Email fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="View Lead Details">
                      <IconButton size="small" color="primary">
                        <Person fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {followups.length > 8 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Showing 8 of {followups.length} follow-ups
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default UpcomingFollowups;